import ReactDOM from "react-dom";
import { AlertCircle, RotateCw, PenLine } from "lucide-react";
import { useState, useEffect } from "react";
// import { MainGuestModal } from "./MainGuestModal"; // DEPRECATED - modal no longer used
import {
  analyzeDocument,
  MAX_ANALYZER_IMAGE_BYTES,
  ACCEPTED_ANALYZER_IMAGE_MIME_TYPES,
} from "../../../services/documentAnalysis";
import { DocumentAnalysisResponse, DocumentIdData, PassportData, DriverLicenseData } from "../../../types/document";
import { FormattedPhoneInput } from "../../../components/FormattedPhoneInput";
import { useInvitationContext } from "../context/InvitationContext";
import { useCreateInvitationOrAccess } from "../../../hooks/useInvitationMutations";
import { fetchAuthSession } from "../../../utils/session";
import { BusinessAccess } from "../../../hooks/useBusinessAccessInvitations";
import type { AxiosResponse } from "axios";
// Layer import removed — overlay now uses ReactDOM.createPortal
import { NotificationService } from "../../../services/notification";
import { DocumentScanner } from "../../../components/DocumentScanner";
import { UploadArea as SharedUploadArea } from "../../../components/UploadArea";
import { StepHeader } from "../../../components/ui/StepHeader";
import { useIsPhone } from '../../../hooks/useIsPhone';
import { useT } from '../../../i18n/admin';
import { PhoneStepShell } from './AccessFormPhone';
import { PhoneContactStep, PhoneGuestStep, PhoneScannedDetails, PhoneTypedDetails } from './NewPassPhoneSteps';
import { Select } from "../../../components/ui/Select";
import { Button } from "../../../components/ui/Button";
import { Notice } from "../../../components/ui/Notice";
import { getApiErrorMessage } from "../../../utils/apiErrors";
import { ExistingClientPicker } from "./ExistingClientPicker";
import { captureSections, choiceAfterTyping, hasPhoneDigits, resolveCaptureChoice, type CaptureChoice } from "./captureChoice";
import { clientUserId, type ClientRow } from "./clientSearch";
import { useUserPermissions } from '../../../hooks/useUserPermissions';
import { picksPassBusiness, resolveInvitationBusinessId } from '../invitationBusiness';
import { passWindow } from '../passDates';
import { writtenDob } from '../../../utils/dateUtils';
import { resolveCompanions, toBusinessGuest, type ResolvedCompanion, type ScannedDoc } from './companionGuests';

// SAM-199: ported off baseui/styletron — this file no longer imports
// `baseui`. Same fields, same validation, same submit payloads and
// navigation; only the presentation moved to Tailwind + the DS
// tokens/components (StepHeader, Select, Button, `.fc`/`.notice` classes).
// A handful of styled-components and helper functions that were never
// actually rendered or called (GuestName, the AddContact/Share/Delete/
// Document icons, the local unused `UploadArea`/`RemoveButton`/`Tooltip`
// styled boxes, the AI-glow keyframes, `showTooltip`/`inputMode` state, and
// `renderDocumentData`/`getDocumentTypeLabel`/`getDocumentTypeId`/
// `isDriverLicenseData` — all dead code with no call site) were dropped
// rather than ported, since they had zero effect either way.

type PhoneCountry = React.ComponentProps<typeof FormattedPhoneInput>['country'];

const DEFAULT_PHONE_COUNTRY = { label: "Dominican Republic", id: "DO", dialCode: "+1" } as PhoneCountry;

// SAM-253: dropdown to choose how the main guest's ID document gets captured.
// Name + phone are ALWAYS required in both modes — the guest is a real person
// either way. What differs is who captures the ID document: the host (Manual
// capture, via scan or typed fallback) or the guest themselves afterwards via
// the SMS link the backend already sends automatically when an invitation is
// created (Share link — the ID-document section is skipped here).
type GuestCaptureMode = 'manual' | 'share_link';

// Default matches the form's original (pre-selector) behavior: the host
// captures the guest's ID document directly in this drawer.
const DEFAULT_GUEST_CAPTURE_MODE: GuestCaptureMode = 'manual';

const GUEST_CAPTURE_MODE_OPTIONS: { label: string; value: GuestCaptureMode }[] = [
  { value: 'share_link', label: 'Share link' },
  { value: 'manual', label: 'Manual capture' },
];

interface Guest {
  firstName: string;
  lastName: string;
  isMainGuest?: boolean;
  documentData?: DocumentIdData | PassportData | DriverLicenseData;
  phoneNumber?: string;
}

interface DocumentScanFormProps {
  mainGuest: Guest;
  companions?: Guest[];
  onComplete: (guest: Guest) => void;
  onSendInvitation?: () => void;
  onSetView?: (view: string, invitationData?: any) => void;
  /** SAM-944: Back from the first phone step, to the Guests step. */
  onBack?: () => void;
}

interface FilePreview {
  file: File;
  preview: string;
  documentData?: DocumentIdData | PassportData | DriverLicenseData;
}

const isDocumentIdData = (data: DocumentIdData | PassportData | DriverLicenseData): data is DocumentIdData => {
  return 'maritalStatus' in data;
};

const isPassportData = (data: DocumentIdData | PassportData | DriverLicenseData): data is PassportData => {
  return 'MRZ' in data;
};

const formatFileSize = (bytes: number): string => {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${Math.ceil(bytes / 1024)} KB`;
};

/** Small spinner for the full-screen "creating invitation" overlay. */
const Spinner = () => (
  <svg className="animate-spin" width={40} height={40} viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth={2}>
    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
    <path d="M12 2a10 10 0 0 1 10 10" strokeOpacity="0.75" />
  </svg>
);

export const DocumentScanForm = ({
  mainGuest,
  companions = [],
  onComplete,
  onSendInvitation,
  onSetView,
  onBack
}: DocumentScanFormProps) => {
  const isPhone = useIsPhone();
  const { t } = useT();
  const { updateInvitation, invitation } = useInvitationContext();
  // SAM-806: a scan made before Next comes back from the context when the step remounts (Back).
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, FilePreview>>(
    () => (invitation?.guestScans as Record<string, FilePreview> | undefined) ?? {}
  );
  // const [showMainGuestModal, setShowMainGuestModal] = useState(false); // DEPRECATED - modal no longer used
  const [isAnalyzing, setIsAnalyzing] = useState<Record<string, boolean>>({});
  const [scanStatus, setScanStatus] = useState<Record<string, 'loading' | 'success' | 'error' | 'unrecognized'>>({});
  const [analyzeError, setAnalyzeError] = useState<Record<string, string>>({});
  // The scanned main guest's phone lives apart from the typed one; restore it with the scan.
  const [contactNumber, setContactNumber] = useState(() =>
    invitation?.guestScans?.[`${mainGuest.firstName}-${mainGuest.lastName}`]?.documentData
      ? invitation?.guests?.mainGuest?.phoneNumber || ''
      : ''
  );
  const [companionPhoneNumbers, setCompanionPhoneNumbers] = useState<Record<string, string>>({});
  const [selectedCountry, setSelectedCountry] = useState<PhoneCountry>(DEFAULT_PHONE_COUNTRY);
  const [companionCountries, setCompanionCountries] = useState<Record<string, PhoneCountry>>({});
  const { mutateAsync: createInvitation } = useCreateInvitationOrAccess();
  const [sessionBusinessId, setBusinessId] = useState<string | null>(null);
  const permissions = useUserPermissions();
  // The tenant Front Desk has no business of its own: the business it picked as destination is the pass's.
  const businessId = resolveInvitationBusinessId({
    isTenantFrontDesk: picksPassBusiness(permissions),
    destination: invitation?.location?.estate_id?.[0] as any,
    selectedBusinessId: sessionBusinessId,
  });
  const [username, setUsername] = useState<string | null>(null);
  const [isCreatingInvitation, setIsCreatingInvitation] = useState(false);
  // Why the server refused the direct create (scanned ID): shown above the button, not only as a toast.
  const [createError, setCreateError] = useState<string | null>(null);

  // Per-guest input mode tracking: records whether each guest used 'scan' or 'manual'
  const [guestInputModes, setGuestInputModes] = useState<Record<string, 'scan' | 'manual'>>(
    () => invitation?.guestInputModes ?? {}
  );
  // SAM-806: finished scans are kept in the context as they happen, so leaving the step (Next, the progress bar)
  // and coming back shows them again.
  useEffect(() => {
    const done = Object.fromEntries(Object.entries(uploadedFiles).filter(([, f]) => f.documentData));
    updateInvitation({ guestScans: done });
  }, [uploadedFiles, updateInvitation]);

  // Scan or typed details, one flow per guest: set by the switch links (see captureChoice.ts).
  const [captureChoices, setCaptureChoices] = useState<Record<string, CaptureChoice>>({});

  // SAM-253: how the main guest's ID document is captured — either here by the
  // host ('manual') or later by the guest via the SMS link ('share_link').
  // Initialized from context (not purely local) and written back into the
  // invitation context on every change so it survives past this component and
  // reaches the submit payload — the bug QA found on the previous attempt was
  // that this lived only in local state and never made it to submit.
  const [guestCaptureMode, setGuestCaptureMode] = useState<GuestCaptureMode>(
    (invitation?.guests?.mainGuest?.captureMode as GuestCaptureMode) || DEFAULT_GUEST_CAPTURE_MODE
  );

  // Manual input data
  const [manualMainGuest, setManualMainGuest] = useState({
    firstName: invitation?.guests?.mainGuest?.firstName || '',
    lastName: invitation?.guests?.mainGuest?.lastName || '',
    phoneNumber: invitation?.guests?.mainGuest?.phoneNumber || ''
  });
  const [manualCompanions, setManualCompanions] = useState<Array<{ firstName: string; lastName: string }>>(
    invitation?.guests?.companions?.companions || []
  );

  // SAM-198 «Existing client»: only a SERVICE booking can book for an existing app user (its
  // on_behalf_of takes {user_id}); the product path's business_access POST always creates a guest.
  const canUseExistingClient = Boolean(invitation?.serviceBooking);
  const [clientMode, setClientMode] = useState<'new' | 'existing'>(
    canUseExistingClient && invitation?.guests?.mainGuest?.userId ? 'existing' : 'new'
  );
  const [existingClient, setExistingClient] = useState<ClientRow | null>(
    canUseExistingClient && invitation?.guests?.mainGuest?.userId
      ? {
          id: invitation.guests.mainGuest.userId,
          name: invitation.guests.mainGuest.firstName,
          last_name: invitation.guests.mainGuest.lastName,
          tel: invitation.guests.mainGuest.phoneNumber,
        }
      : null
  );
  const pickExistingClient = (row: ClientRow | null) => {
    setExistingClient(row);
    // The rest of the flow (summary rail, detail, confirmation) reads the main guest's name and
    // phone; the booking itself only sends the user id.
    setManualMainGuest(row
      ? { firstName: (row.name || '').trim(), lastName: (row.last_name || '').trim(), phoneNumber: (row.tel || '').trim() }
      : { firstName: '', lastName: '', phoneNumber: '' });
  };
  const switchClientMode = (mode: 'new' | 'existing') => {
    if (mode === clientMode) return;
    setClientMode(mode);
    if (existingClient) pickExistingClient(null);
  };

  // Current companion index for wizard-style navigation (-1 = main guest, 0+ = companions)
  const [currentCompanionIndex, setCurrentCompanionIndex] = useState(-1);
  // SAM-944: on a phone each guest goes guest (photo) -> form (details) -> contact (main guest only).
  const [phoneSub, setPhoneSub] = useState<'guest' | 'form' | 'contact'>('guest');
  useEffect(() => { setPhoneSub('guest'); }, [currentCompanionIndex]);

  // Debug: Log invitation context on component mount and when it changes
  useEffect(() => {
  }, [invitation]);

  // Fetch the business ID and username from sessionStorage and token
  useEffect(() => {
    const getAuthInfo = async () => {
      try {
        // Get the selected business ID from sessionStorage
        const selectedBusinessId = sessionStorage.getItem('selectedBusinessId');
        if (selectedBusinessId) {
          setBusinessId(selectedBusinessId);
        }

        // Get username from token
        const session = await fetchAuthSession();
        if (session.tokens?.idToken) {
          const token = session.tokens.idToken.toString();
          const tokenParts = token.split('.');
          if (tokenParts.length === 3) {
            const claims = JSON.parse(atob(tokenParts[1]));
            const userUsername = claims['cognito:username'];
            if (userUsername) {
              setUsername(userUsername);
            }
          }
        }
      } catch (error) {
        console.error("Error getting auth info:", error);
      }
    };
    getAuthInfo();
  }, []);

  // A response counts as "unrecognized" when the service responds successfully
  // but couldn't extract any meaningful field from the image (e.g. a non-ID photo,
  // a blurry scan, or an unsupported document).
  const isDocumentDataEmpty = (data?: { name?: string; LastName?: string; number?: string } | null): boolean => {
    if (!data) return true;
    const name = data.name?.trim?.() ?? '';
    const lastName = data.LastName?.trim?.() ?? '';
    const number = data.number?.trim?.() ?? '';
    return !name && !lastName && !number;
  };

  const handleFileUpload = async (file: File, guestId: string) => {
    if (file) {
      if (file.size > MAX_ANALYZER_IMAGE_BYTES) {
        setAnalyzeError(prev => ({
          ...prev,
          [guestId]: `Image is too large (${formatFileSize(file.size)}). Maximum allowed size is ${formatFileSize(MAX_ANALYZER_IMAGE_BYTES)}.`
        }));
        return;
      }

      if (file.type && !ACCEPTED_ANALYZER_IMAGE_MIME_TYPES.includes(file.type)) {
        setAnalyzeError(prev => ({
          ...prev,
          [guestId]: 'Unsupported file format. Please upload a JPG or PNG image.'
        }));
        return;
      }


      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = (reader.result as string).split(',')[1]; // Remove data:image/...;base64,

        setUploadedFiles(prev => ({
          ...prev,
          [guestId]: {
            file,
            preview: reader.result as string
          }
        }));

        try {
          setIsAnalyzing(prev => ({ ...prev, [guestId]: true }));
          setScanStatus(prev => ({ ...prev, [guestId]: 'loading' }));
          setAnalyzeError(prev => ({ ...prev, [guestId]: '' }));

          const response = await analyzeDocument({
            image: base64Data,
            filename: file.name
          });

          if (isDocumentDataEmpty(response?.data)) {
            // Service responded but couldn't read the document — do not preload data,
            // let the user retake the photo or switch to manual entry.
            setScanStatus(prev => ({ ...prev, [guestId]: 'unrecognized' }));
            setAnalyzeError(prev => ({
              ...prev,
              [guestId]: "We couldn't recognize this document. Try a clearer photo or enter the guest's details manually."
            }));
          } else {
            setUploadedFiles(prev => ({
              ...prev,
              [guestId]: {
                file,
                preview: reader.result as string,
                documentData: response.data
              }
            }));
            setScanStatus(prev => ({ ...prev, [guestId]: 'success' }));
            NotificationService.success('Document analyzed successfully.');
          }
        } catch (error) {
          console.error('Error analyzing document:', error);
          setScanStatus(prev => ({ ...prev, [guestId]: 'error' }));
          setAnalyzeError(prev => ({
            ...prev,
            [guestId]: "We couldn't analyze this document. Check your connection and try again, or enter the details manually."
          }));
        } finally {
          window.setTimeout(() => {
            setIsAnalyzing(prev => ({ ...prev, [guestId]: false }));
          }, 1400);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Re-runs the analysis for a guest's already-selected file (service error retry).
  const handleRetryAnalysis = (guestId: string) => {
    const existingFile = uploadedFiles[guestId]?.file;
    if (existingFile) {
      handleFileUpload(existingFile, guestId);
    }
  };

  // Clears a failed/unrecognized scan so the guest can be captured via the
  // manual input fields instead, without leaving the form stuck on the error.
  const handleUseManualInput = (guestId: string) => {
    handleRemoveFile(guestId);
    // The scan's contact number goes with it; the typed form starts empty.
    if (currentCompanionIndex === -1) setContactNumber("");
    setGuestInputModes(prev => ({ ...prev, [guestId]: 'manual' }));
    setCaptureChoices(prev => ({ ...prev, [guestId]: 'manual' }));
  };

  // «Scan an ID instead»: the typed details are cleared and only the scan is offered.
  const handleUseScan = (guestId: string) => {
    if (currentCompanionIndex === -1) {
      setManualMainGuest(prev => ({ ...prev, firstName: '', lastName: '', phoneNumber: '' }));
    } else {
      setManualCompanions(prev => {
        const updated = [...prev];
        updated[currentCompanionIndex] = { firstName: '', lastName: '' };
        return updated;
      });
    }
    setCaptureChoices(prev => ({ ...prev, [guestId]: 'scan' }));
  };

  // Emptying the typed fields drops the explicit «manual» choice: both options are offered again.
  const syncChoiceWithTyping = (guestId: string, typed: boolean) => {
    setCaptureChoices(prev => {
      const next = choiceAfterTyping(prev[guestId], typed);
      if (next === prev[guestId]) return prev;
      const updated = { ...prev };
      if (next) updated[guestId] = next; else delete updated[guestId];
      return updated;
    });
  };

  const typedMainGuest = (g: { firstName: string; lastName: string; phoneNumber: string }, dialCode: string) =>
    Boolean(g.firstName.trim() || g.lastName.trim() || hasPhoneDigits(g.phoneNumber, dialCode));

  const updateManualMainGuest = (patch: Partial<typeof manualMainGuest>, dialCode = selectedCountry.dialCode) => {
    const next = { ...manualMainGuest, ...patch };
    setManualMainGuest(next);
    syncChoiceWithTyping(activeGuestId, typedMainGuest(next, dialCode));
  };

  const updateManualCompanion = (index: number, patch: { firstName?: string; lastName?: string }) => {
    const updated = [...manualCompanions];
    updated[index] = { ...updated[index], ...patch };
    setManualCompanions(updated);
    syncChoiceWithTyping(activeGuestId, Boolean(updated[index].firstName?.trim() || updated[index].lastName?.trim()));
  };

  const handleRemoveFile = (guestId: string) => {
    // Removing the scan leaves nothing captured: both options are offered again.
    setCaptureChoices(prev => {
      const next = { ...prev };
      delete next[guestId];
      return next;
    });
    setUploadedFiles(prev => {
      const newFiles = { ...prev };
      delete newFiles[guestId];
      return newFiles;
    });
    setAnalyzeError(prev => {
      const newErrors = { ...prev };
      delete newErrors[guestId];
      return newErrors;
    });
    setScanStatus(prev => {
      const newStatus = { ...prev };
      delete newStatus[guestId];
      return newStatus;
    });
  };

  // Function to validate phone number based on country
  const isValidPhoneNumber = (phoneNumber: string, country: PhoneCountry): boolean => {
    if (!phoneNumber || phoneNumber.trim() === '') return false;

    // Remove all non-digit characters
    const digits = phoneNumber.replace(/\D/g, '');

    // Remove country code from the beginning if present
    let cleanDigits = digits;
    const countryDialCode = country.dialCode.replace('+', '');
    if (digits.startsWith(countryDialCode)) {
      cleanDigits = digits.slice(countryDialCode.length);
    }

    // Validate based on country
    if (country.id === 'DO') {
      // Dominican Republic: expects 10 digits (not counting country code +1)
      return cleanDigits.length === 10;
    } else {
      // For other countries, use a general validation (7-15 digits)
      return cleanDigits.length >= 7 && cleanDigits.length <= 15;
    }
  };

  // Resolve main guest data based on input mode (scan vs manual)
  const resolveMainGuestData = (modes: Record<string, 'scan' | 'manual'>) => {
    const mainGuestId = `${mainGuest.firstName}-${mainGuest.lastName}`;
    const mode = modes[mainGuestId] || (uploadedFiles[mainGuestId]?.documentData ? 'scan' : 'manual');

    if (mode === 'scan') {
      const docData = uploadedFiles[mainGuestId]?.documentData;
      return {
        firstName: docData?.name || mainGuest.firstName,
        lastName: docData?.LastName || mainGuest.lastName,
        phoneNumber: contactNumber,
        idnum: docData?.number || null,
        nationality: (docData as any)?.nationality || null,
        birthdate: docData?.DOB || null,
        photo_url: docData?.picture || null,
        isScanned: true,
      };
    } else {
      return {
        firstName: manualMainGuest.firstName,
        lastName: manualMainGuest.lastName,
        phoneNumber: manualMainGuest.phoneNumber,
        idnum: null,
        nationality: null,
        birthdate: null,
        photo_url: null,
        isScanned: false,
      };
    }
  };

  // Resolve companions data, respecting each companion's individual input mode
  const resolveCompanionsData = (modes: Record<string, 'scan' | 'manual'>) =>
    resolveCompanions({
      companions,
      modes,
      docOf: (id) => uploadedFiles[id]?.documentData as ScannedDoc | undefined,
      phones: companionPhoneNumbers,
      manual: manualCompanions,
    });

  const handleContinue = async (resolvedCompanions: ResolvedCompanion[]) => {
    const mainGuestId = `${mainGuest.firstName}-${mainGuest.lastName}`;
    const mainGuestFile = uploadedFiles[mainGuestId];

    if (mainGuestFile?.documentData && businessId) {
      const documentData = mainGuestFile.documentData;
      const updatedMainGuest = {
        ...mainGuest,
        documentData
      };

      try {
        setIsCreatingInvitation(true);
        setCreateError(null);

        // Preparar los datos para la invitación
        // The days chosen in «How long?» (it used to ignore them: now + 5 min, one day)
        const { startDate, endDate } = passWindow(invitation?.duration);

        // Format business_guests as an array of guest objects
        const businessGuests = resolvedCompanions.map(toBusinessGuest);

        // Log the final companions data for debugging

        // Get the selected estate ID - always include for residential invitations
        const selectedEstateId = invitation?.location?.estate_id?.[0]?.id;
        const endpointType = sessionStorage.getItem('invitationEndpointType') || 'business_access';


        // Validation: For residential invitations, estate_id is required
        if (endpointType === 'invitation' && !selectedEstateId) {
          NotificationService.error("Please select a specific property for residential passes");
          setIsCreatingInvitation(false);
          return;
        }

        const invitationData: any = {
          // SAM-1304: the pass of an event belongs to the event's business and carries its id (the server re-checks both)
          business_id: invitation?.selectedEvent?.business_id ?? businessId,
          event_id: invitation?.selectedEvent?.id ?? null,
          // SAM-199: `services_id: 3` removed — the DB column is `service_id` (uuid FK), not
          // `services_id`, and businessAccess/lambda_function.py's `_normalize_service_id` only
          // keeps a `services_id`/`service_id` value when it parses as a UUID (SAM-461). The
          // literal integer 3 never did, so it was silently dropped server-side on every
          // request — this line has never had any effect. Verified against
          // sam-lambda-functions functions/businessAccess/lambda_function.py.
          id_channel: 1,
          created_by: username || undefined,
          modified_by: null,
          business_access_type_id: invitation?.accessType?.[0]?.id || 2,
          start_date: startDate,
          end_date: endDate,
          business_access_status_id: 2,
          children_count: invitation?.guests?.companions?.childrens || 0,
          main_guest: {
            name: documentData.name,
            last_name: documentData.LastName,
            title_es: 'Principal',
            tel: contactNumber,
            photo_url: documentData.picture,
            idnum: documentData.number || null,
            nationality: (documentData as any)?.nationality || null,
            birthdate: documentData.DOB || null,
            // This direct-create path only runs when a document was scanned
            // here by the host, which is only offered in 'manual' mode.
            capture_mode: 'manual',
          },
          business_guests: businessGuests,
          is_analyzed: true
        };

        // For residential (invitation endpoint), always include estate_id
        // For business (business_access endpoint), estate_id will be excluded in the mutation hook
        if (endpointType === 'invitation') {
          invitationData.estate_id = selectedEstateId;
        } else {
        }

        // Enviar la invitación al API
        const response = await createInvitation(invitationData);

        // Handle different response formats from combined hook
        let createdInvitation: any;
        if ((response as any)?.data?.[0]) {
          // business_access endpoint response
          createdInvitation = (response as any).data[0];
        } else if ((response as any)?.id) {
          // invitation endpoint response
          createdInvitation = response;
        } else if (response) {
          // fallback
          createdInvitation = response;
        }

        if (createdInvitation) {
          // Actualizar el contexto con los datos de la invitación
          updateInvitation({
            legacy_code: createdInvitation.legacy_code,
            code: createdInvitation.id,
            business_name: createdInvitation.business_name
          });

          // Note: SMS is sent automatically by backend when is_analyzed: true
          // No need to send SMS manually from frontend for document scan flow

          // Llamar a onComplete con los datos actualizados
          onComplete({
            ...updatedMainGuest,
            phoneNumber: contactNumber
          });

          // Cambiar a la vista de COMPLETED
          if (onSetView) {
            onSetView('COMPLETED', createdInvitation);
          }
        }
      } catch (error) {
        console.error('Error al crear la invitación:', error);
        setCreateError(getApiErrorMessage(error, 'The pass could not be created. Try again.'));
        NotificationService.error("Error creating pass. Please try again.");
      } finally {
        setIsCreatingInvitation(false);
      }
    }
  };

  const renderGuestUpload = (guest: Guest) => {
    const guestId = `${guest.firstName}-${guest.lastName}`;
    const uploadedFile = uploadedFiles[guestId];
    const isAnalyzingFile = isAnalyzing[guestId];
    const error = analyzeError[guestId];
    const status = scanStatus[guestId];
    const documentData = uploadedFile?.documentData;

    const handleCompanionPhoneChange = (value: string, country: PhoneCountry) => {
      setCompanionPhoneNumbers(prev => ({
        ...prev,
        [guestId]: value
      }));

      setCompanionCountries(prev => ({
        ...prev,
        [guestId]: country
      }));
    };

    return (
      <div key={guestId} className="mb-6 p-6" style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-8)' }}>
        <div className="mb-2 text-sm" style={{ color: 'var(--ink-500)' }}>
          Please add the ID document for {guest.isMainGuest ? 'the main guest' : 'the companion'}
        </div>
        {!uploadedFile ? (
          <>
            <SharedUploadArea
              onFileSelect={(file) => handleFileUpload(file, guestId)}
              accept=".png,.jpg,.jpeg"
              title={`ID document for ${guest.isMainGuest ? 'main guest' : 'companion'}`}
              hint="Drag & drop or click to upload"
              color="var(--tenant-primary)"
            />
            <div className="mt-2 text-xs text-center" style={{ color: 'var(--ink-400)' }}>
              Accepted formats: JPG, PNG · Max size: {formatFileSize(MAX_ANALYZER_IMAGE_BYTES)}
            </div>
            {error && (
              <div className="notice notice-error mt-3">
                <div>{error}</div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="w-full mx-auto flex flex-col gap-2" style={{ maxWidth: 320 }}>
              <div
                className="relative w-full overflow-hidden"
                style={{ aspectRatio: '1.586 / 1', borderRadius: 'var(--r-8)', background: 'var(--surface)', border: `2px solid var(--line)` }}
              >
                <img src={uploadedFile.preview} alt="Document preview" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => handleRemoveFile(guestId)}
                  aria-label="Remove image"
                  title="Remove"
                  className="absolute flex items-center justify-center"
                  style={{
                    top: 10,
                    right: 10,
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.95)',
                    border: '1px solid rgba(0,0,0,0.08)',
                    boxShadow: 'var(--sh-1)',
                    color: 'var(--ink-700)',
                    padding: 0,
                    cursor: 'pointer',
                  }}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                    <path d="M6 6 L18 18" />
                    <path d="M18 6 L6 18" />
                  </svg>
                </button>
              </div>
            </div>

            {error && (status === 'error' || status === 'unrecognized') && (
              <div className={`notice mt-4 ${status === 'unrecognized' ? 'notice-warn' : 'notice-error'}`}>
                <div className="flex items-center gap-2">
                  <AlertCircle size={18} style={{ flexShrink: 0 }} />
                  <span>{error}</span>
                </div>
                <div className="flex gap-2 flex-wrap mt-3">
                  {status === 'error' && (
                    <Button type="button" variant="ghost" onClick={() => handleRetryAnalysis(guestId)} icon={<RotateCw size={14} />}>
                      Try again
                    </Button>
                  )}
                  <Button type="button" variant="ghost" onClick={() => handleUseManualInput(guestId)} icon={<PenLine size={14} />}>
                    Enter details manually
                  </Button>
                </div>
              </div>
            )}

            {documentData && (
              <div className="grid gap-4 mt-6" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="fc">
                  <label>First Name</label>
                  <input value={documentData.name || ''} readOnly placeholder="First Name" />
                </div>
                <div className="fc">
                  <label>Last Name</label>
                  <input value={documentData.LastName || ''} readOnly placeholder="Last Name" />
                </div>
                <div className="fc">
                  <label>Nationality</label>
                  <input
                    value={(isDocumentIdData(documentData) || isPassportData(documentData)) ? documentData.nationality : ''}
                    readOnly
                    placeholder="Nationality"
                  />
                </div>
                <div className="fc">
                  <label>Date of birth</label>
                  <input value={writtenDob(documentData.DOB)} readOnly placeholder="Date of birth" />
                </div>
                <div className="fc">
                  <label>ID type</label>
                  <Select
                    value={isDocumentIdData(documentData) ? 'documentId' : isPassportData(documentData) ? 'passport' : 'driverLicense'}
                    disabled
                    options={[
                      { label: 'Passport', value: 'passport' },
                      { label: 'Driver License', value: 'driverLicense' },
                      { label: 'Document ID', value: 'documentId' },
                    ]}
                    onChange={() => {}}
                  />
                </div>
                <div className="fc">
                  <label>ID Number</label>
                  <input value={documentData.number || ''} readOnly placeholder="ID Number" />
                </div>
                <div className="fc" style={{ gridColumn: '1 / -1' }}>
                  <label>Contact number</label>
                  {guest.isMainGuest ? (
                    <FormattedPhoneInput
                      value={contactNumber}
                      country={DEFAULT_PHONE_COUNTRY}
                      onChange={(value, country) => {
                        setContactNumber(value);
                        setSelectedCountry(country);
                      }}
                      required
                      placeholder="Enter contact number"
                      showFormatHint={false}
                      error={contactNumber && !isValidPhoneNumber(contactNumber, selectedCountry) ?
                        (selectedCountry.id === 'DO' ?
                          'Please enter a complete phone number (10 digits)' :
                          'Please enter a complete phone number'
                        ) : false
                      }
                    />
                  ) : (
                    <FormattedPhoneInput
                      value={companionPhoneNumbers[guestId] || ''}
                      country={companionCountries[guestId] || DEFAULT_PHONE_COUNTRY}
                      onChange={(value, country) => handleCompanionPhoneChange(value, country)}
                      placeholder="Enter contact number (optional)"
                      showFormatHint={false}
                      error={companionPhoneNumbers[guestId] && !isValidPhoneNumber(companionPhoneNumbers[guestId], companionCountries[guestId] || selectedCountry) ?
                        (companionCountries[guestId]?.id === 'DO' || selectedCountry.id === 'DO' ?
                          'Please enter a complete phone number (10 digits)' :
                          'Please enter a complete phone number'
                        ) : false
                      }
                    />
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // Check if current guest (based on currentCompanionIndex) has document uploaded
  const canProceedCurrentGuest = () => {
    let currentGuest;
    if (currentCompanionIndex === -1) {
      currentGuest = mainGuest;
    } else {
      currentGuest = companions[currentCompanionIndex];
    }

    if (!currentGuest) return false;

    const guestId = `${currentGuest.firstName}-${currentGuest.lastName}`;
    const hasDocument = uploadedFiles[guestId]?.documentData;
    const isAnalyzingCurrent = isAnalyzing[guestId];

    // For main guest, also check phone number
    if (currentCompanionIndex === -1) {
      const hasValidPhoneNumber = isValidPhoneNumber(contactNumber, selectedCountry);
      return hasDocument && hasValidPhoneNumber && !isAnalyzingCurrent;
    }

    // For companions, just check document
    return hasDocument && !isAnalyzingCurrent;
  };

  const analyzingGuestId = Object.keys(isAnalyzing).find(id => isAnalyzing[id]);

  // Scan or typed details for the guest on screen: one flow, the other is hidden (captureChoice.ts).
  const activeGuest = currentCompanionIndex === -1 ? mainGuest : companions[currentCompanionIndex];
  const activeGuestId = activeGuest ? `${activeGuest.firstName}-${activeGuest.lastName}` : '';
  const activeTyped = currentCompanionIndex === -1
    ? typedMainGuest(manualMainGuest, selectedCountry.dialCode)
    : Boolean(manualCompanions[currentCompanionIndex]?.firstName?.trim() || manualCompanions[currentCompanionIndex]?.lastName?.trim());
  const activeChoice = resolveCaptureChoice({
    explicit: captureChoices[activeGuestId],
    scanned: Boolean(uploadedFiles[activeGuestId]),
    typed: activeTyped,
  });
  const scanAllowed = currentCompanionIndex !== -1 || guestCaptureMode === 'manual';
  const sections = captureSections({ choice: activeChoice, scanAllowed });

  // «Next» of the desktop form, also the last button of the phone steps.
  const advance = () => {
                // Determine current guest's mode (an existing client is never a scan)
                const usingExistingClient = currentCompanionIndex === -1 && canUseExistingClient && clientMode === 'existing';
                const isCurrentGuestScanned = usingExistingClient ? false : canProceedCurrentGuest();
                let currentGuestId: string;
                if (currentCompanionIndex === -1) {
                  currentGuestId = `${mainGuest.firstName}-${mainGuest.lastName}`;
                } else {
                  const comp = companions[currentCompanionIndex];
                  currentGuestId = `${comp.firstName}-${comp.lastName}`;
                }

                // Record the current guest's input mode
                const updatedModes = {
                  ...guestInputModes,
                  [currentGuestId]: isCurrentGuestScanned ? 'scan' as const : 'manual' as const
                };
                setGuestInputModes(updatedModes);

                // Check if we need to advance to the next guest
                const isLastGuest = currentCompanionIndex >= companions.length - 1;
                const isMainGuestWithCompanions = currentCompanionIndex === -1 && companions.length > 0;

                if (isMainGuestWithCompanions) {
                  // Main guest done, advance to first companion
                  setCurrentCompanionIndex(0);
                } else if (!isLastGuest) {
                  // More companions to process
                  setCurrentCompanionIndex(currentCompanionIndex + 1);
                } else {
                  // Last guest done — resolve all data using per-guest modes and proceed
                  const resolvedMain = resolveMainGuestData(updatedModes);
                  const resolvedCompanions = resolveCompanionsData(updatedModes);

                  const requiresPayment =
                    invitation?.accessType?.[0]?.label === 'Client Booking' &&
                    invitation?.product?.[0]?.price &&
                    invitation.product[0].price > 0;

                  // Check if ALL guests were scanned (for the handleContinue direct-create path)
                  const allScanned = resolvedMain.isScanned && resolvedCompanions.every(c => uploadedFiles[`${c.firstName}-${c.lastName}`]?.documentData || updatedModes[`${c.firstName}-${c.lastName}`] === 'scan');

                  // Build merged context update
                  // SAM-842: the scan fields and phone travel with each companion to the detail / payment step.
                  const mergedCompanionsList = resolvedCompanions.map(c => ({
                    firstName: c.firstName,
                    lastName: c.lastName,
                    tel: c.tel,
                    idnum: c.idnum,
                    nationality: c.nationality,
                    birthdate: c.birthdate,
                    photo_url: c.photo_url,
                  }));

                  updateInvitation({
                    guestInputModes: updatedModes,
                    guests: {
                      mainGuest: {
                        firstName: resolvedMain.firstName,
                        lastName: resolvedMain.lastName,
                        phoneNumber: resolvedMain.phoneNumber,
                        idnum: resolvedMain.idnum,
                        nationality: resolvedMain.nationality,
                        birthdate: resolvedMain.birthdate,
                        photo_url: resolvedMain.photo_url,
                        // SAM-253: propagate the chosen capture mode into context so
                        // it reaches the submit payload instead of dying as local state.
                        captureMode: guestCaptureMode,
                        // SAM-198: set only for «Existing client» (service booking).
                        userId: canUseExistingClient && clientMode === 'existing' && existingClient
                          ? clientUserId(existingClient) : null,
                      },
                      companions: {
                        adults: companions.length + 1,
                        childrens: invitation?.guests?.companions?.childrens || 0,
                        companions: mergedCompanionsList
                      }
                    }
                  });

                  // Decide navigation: if main guest was scanned, try the direct-create path;
                  // otherwise go through the standard detail/payment flow
                  if (resolvedMain.isScanned && !requiresPayment) {
                    // Direct create via handleContinue (existing scan-all path)
                    handleContinue(resolvedCompanions);
                  } else if (requiresPayment && onSetView) {
                    onSetView('PAYMENT');
                  } else if (onSendInvitation) {
                    onSendInvitation();
                  }
                }
              };

  // SAM-944 / SAM-1171: New pass at 375 px, steps 4 and 5 (canvas v256: Main guest x1365/x1820, Contact x2275).
  // Same state and rules as the desktop form; `advance` is the desktop «Next».
  if (isPhone) {
    const isMain = currentCompanionIndex === -1;
    const id = activeGuestId;
    const doc = uploadedFiles[id]?.documentData as (Partial<DocumentIdData> & { type?: string }) | undefined;
    const scanned = Boolean(doc);
    const usingExisting = isMain && canUseExistingClient && clientMode === 'existing';
    const title = isMain ? t('booking.phone.mainGuest') : t('booking.phone.companionN', { n: currentCompanionIndex + 2 });
    const sub = phoneSub === 'guest' && scanned ? 'form' : phoneSub;
    const goBack = () => {
      if (sub === 'contact') return setPhoneSub('form');
      if (sub === 'form' && !usingExisting) {
        if (scanned) handleRemoveFile(id);
        return setPhoneSub('guest');
      }
      if (usingExisting) return switchClientMode('new');
      if (isMain) return onBack?.();
      return setCurrentCompanionIndex(currentCompanionIndex - 1);
    };
    const shell = (step: number, body: React.ReactNode, continueDisabled: boolean, onContinue?: () => void) => (
      <div className="relative h-full">
        <PhoneStepShell step={step} onBack={goBack} onClose={() => onBack?.()} onContinue={onContinue} continueDisabled={continueDisabled}>
          {body}
        </PhoneStepShell>
        {analyzingGuestId && (
          <div className="absolute inset-0 z-40 flex items-start justify-center bg-white/90 pt-20">
            <DocumentScanner status={scanStatus[analyzingGuestId] === 'unrecognized' ? 'error' : (scanStatus[analyzingGuestId] ?? 'loading')} size="md" color="var(--tenant-primary)" />
          </div>
        )}
        {isCreatingInvitation && <div className="absolute inset-0 z-40 flex items-center justify-center bg-white/80 text-base font-medium">Creating invitation...</div>}
      </div>
    );

    if (usingExisting) {
      return shell(4, (
        <div className="flex flex-col gap-3 p-4">
          <div className="text-2xl font-bold text-[var(--ink-900)]">{title}</div>
          <ExistingClientPicker selected={existingClient} onSelect={pickExistingClient} />
        </div>
      ), !existingClient, advance);
    }
    if (sub === 'guest') {
      // Continue is not offered here: the photo, the registration link or the typed details move on by themselves.
      return shell(4, (
        <PhoneGuestStep
          title={title}
          analyzing={Boolean(isAnalyzing[id])}
          error={analyzeError[id] || undefined}
          onPhoto={(file) => handleFileUpload(file, id)}
          onContacts={isMain && canUseExistingClient ? () => switchClientMode('existing') : undefined}
          onShareLink={isMain ? () => { setGuestCaptureMode('share_link'); handleUseManualInput(id); setPhoneSub('form'); } : undefined}
          onManual={() => { if (isMain) setGuestCaptureMode('manual'); handleUseManualInput(id); setPhoneSub('form'); }}
        />
      ), false, undefined);
    }
    const mainPhoneValid = isValidPhoneNumber(scanned ? contactNumber : manualMainGuest.phoneNumber, selectedCountry);
    if (sub === 'contact') {
      return shell(5, (
        <PhoneContactStep>
          <FormattedPhoneInput
            value={scanned ? contactNumber : manualMainGuest.phoneNumber}
            country={selectedCountry}
            onChange={(value, country) => {
              if (scanned) setContactNumber(value);
              else updateManualMainGuest({ phoneNumber: value }, country.dialCode);
              setSelectedCountry(country);
            }}
            required
            placeholder={t('booking.phone.phoneNumber')}
            showFormatHint={false}
            error={(scanned ? contactNumber : manualMainGuest.phoneNumber) && !mainPhoneValid ? t('booking.phone.phoneIncomplete') : false}
          />
        </PhoneContactStep>
      ), !mainPhoneValid, advance);
    }
    // form
    if (scanned) {
      const rows = [
        { label: t('booking.phone.firstName'), value: doc?.name || '' },
        { label: t('booking.phone.lastName'), value: doc?.LastName || '' },
        { label: t('booking.phone.idNumber'), value: doc?.number || '' },
        { label: t('booking.phone.nationality'), value: doc?.nationality || '' },
        { label: t('booking.phone.dob'), value: doc?.DOB ? writtenDob(doc.DOB) : '' },
      ];
      return shell(4, (
        <PhoneScannedDetails title={title} preview={uploadedFiles[id]?.preview} rows={rows} onRescan={(file) => handleFileUpload(file, id)} />
      ), Boolean(analyzingGuestId), () => (isMain ? setPhoneSub('contact') : advance()));
    }
    const first = isMain ? manualMainGuest.firstName : manualCompanions[currentCompanionIndex]?.firstName || '';
    const last = isMain ? manualMainGuest.lastName : manualCompanions[currentCompanionIndex]?.lastName || '';
    return shell(4, (
      <PhoneTypedDetails
        title={title}
        firstName={first}
        lastName={last}
        hint={isMain && guestCaptureMode === 'share_link' ? t('booking.phone.registrationHint') : undefined}
        onFirstName={(v) => (isMain ? updateManualMainGuest({ firstName: v }) : updateManualCompanion(currentCompanionIndex, { firstName: v }))}
        onLastName={(v) => (isMain ? updateManualMainGuest({ lastName: v }) : updateManualCompanion(currentCompanionIndex, { lastName: v }))}
      />
    ), !first.trim() || !last.trim(), () => (isMain ? setPhoneSub('contact') : advance()));
  }

  return (
    <>
    <div className="w-full flex relative">
      {analyzingGuestId && (
        <div
          className="absolute inset-0 flex flex-col items-center"
          style={{
            justifyContent: 'flex-start',
            paddingTop: 80,
            background: 'rgba(255, 255, 255, 0.92)',
            backdropFilter: 'blur(3px)',
            zIndex: 50,
          }}
        >
          <DocumentScanner
            status={scanStatus[analyzingGuestId] === 'unrecognized' ? 'error' : (scanStatus[analyzingGuestId] ?? 'loading')}
            size="md"
            color="var(--tenant-primary)"
          />
        </div>
      )}
      <div className="flex-1 w-full mx-auto" style={{ maxWidth: 800 }}>
          {/* Dynamic Title based on current guest */}
          <StepHeader title={currentCompanionIndex === -1 ? 'Main Guest' : `Companion ${currentCompanionIndex + 1}`} />

          {/* SAM-198: New client (typed / scanned, today's flow) or an existing app user. */}
          {currentCompanionIndex === -1 && canUseExistingClient && (
            <div role="group" aria-label="Client" className="mb-4 grid grid-cols-2 gap-1 p-[3px] rounded-[10px] bg-[var(--line-subtle)] max-w-md">
              {([['new', 'New client'], ['existing', 'Existing client']] as const).map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  aria-pressed={clientMode === k}
                  onClick={() => switchClientMode(k)}
                  className={`h-9 px-3 rounded-lg text-[13px] font-semibold border-0 cursor-pointer ${clientMode === k
                    ? 'bg-white text-[var(--ink-900)] shadow-sm' : 'bg-transparent text-[var(--ink-500)]'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {currentCompanionIndex === -1 && canUseExistingClient && clientMode === 'existing' ? (
            <div className="mb-6 flex flex-col gap-2" data-testid="existing-client">
              <p className="m-0 text-[13px] text-[var(--ink-500)]">
                Book for someone who already has the app. The booking goes to their account, with their name, phone and ID on file.
              </p>
              <ExistingClientPicker selected={existingClient} onSelect={pickExistingClient} />
            </div>
          ) : (
          <>
          {/* SAM-253: mode selector — main guest only. Companions don't get this
              choice since they have no phone number of their own to send a
              share link to; their ID document is always captured by the host. */}
          {currentCompanionIndex === -1 && (
            <div className="mb-4 fc">
              <label>How should we capture this guest's ID document?</label>
              <Select
                value={guestCaptureMode}
                options={GUEST_CAPTURE_MODE_OPTIONS}
                onChange={(e) => setGuestCaptureMode(e.target.value as GuestCaptureMode)}
              />
              {guestCaptureMode === 'share_link' && (
                <p className="cap">
                  We'll text this guest a link once the invitation is created so they can
                  scan or enter their own ID document. Name and phone number are still
                  required here to create the invitation.
                </p>
              )}
            </div>
          )}

          {/* Scan Document Section — for the main guest this is only offered in
              Manual capture mode; in Share link mode the guest completes their
              own ID document later via the SMS link, so this is skipped here. */}
          {sections.showScan && (
            <>
              <div className="flex justify-between items-center mb-6 py-2">
                <h3 className="m-0" style={{ fontSize: 18, fontWeight: 600, color: 'var(--ink-900)' }}>Scan Document</h3>
              </div>
              {currentCompanionIndex === -1 ? (
                renderGuestUpload({ ...mainGuest, isMainGuest: true })
              ) : (
                companions[currentCompanionIndex] && renderGuestUpload({ ...companions[currentCompanionIndex], isMainGuest: false })
              )}

              {/* OR Divider — only while neither flow has been picked */}
              {sections.showOr && (
                <div className="flex items-center text-center my-8">
                  <div className="flex-1" style={{ borderBottom: '1px solid var(--line)' }} />
                  <span className="px-4" style={{ fontSize: 16, fontWeight: 500, color: 'var(--ink-500)' }}>or</span>
                  <div className="flex-1" style={{ borderBottom: '1px solid var(--line)' }} />
                </div>
              )}
              {!sections.showManual && (
                <button
                  type="button"
                  className="text-xs underline font-medium mb-6"
                  style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', color: 'var(--ink-700)' }}
                  onClick={() => handleUseManualInput(activeGuestId)}
                >
                  Enter details manually instead
                </button>
              )}
            </>
          )}

          {sections.showManual && (
          <>
          {/* SAM-253: renamed from "Share Link" to "Guest Details" — that title
              never matched this manual-entry form. Name, last name and phone
              are always required here in BOTH capture modes; what the mode
              selector above controls is only whether the ID-document section
              is offered. */}
          <div className="flex justify-between items-center mb-6 py-2">
            <h3 className="m-0" style={{ fontSize: 18, fontWeight: 600, color: 'var(--ink-900)' }}>
              {currentCompanionIndex === -1 ? 'Guest Details' : 'Manual Input'}
            </h3>
          </div>

          {currentCompanionIndex === -1 ? (
            /* Main Guest Form */
            <div className="mb-6 p-6" style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-8)' }}>
              <div className="grid gap-4" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="fc">
                  <label>First Name</label>
                  <input
                    value={manualMainGuest.firstName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateManualMainGuest({ firstName: e.target.value })
                    }
                    placeholder="Enter first name"
                  />
                </div>
                <div className="fc">
                  <label>Last Name</label>
                  <input
                    value={manualMainGuest.lastName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateManualMainGuest({ lastName: e.target.value })
                    }
                    placeholder="Enter last name"
                  />
                </div>
                <div className="fc" style={{ gridColumn: '1 / -1' }}>
                  <label>Contact Number</label>
                  <FormattedPhoneInput
                    value={manualMainGuest.phoneNumber}
                    country={selectedCountry}
                    onChange={(value, country) => {
                      updateManualMainGuest({ phoneNumber: value }, country.dialCode);
                      setSelectedCountry(country);
                    }}
                    required
                    placeholder="Phone number"
                    showFormatHint={false}
                    error={manualMainGuest.phoneNumber && !isValidPhoneNumber(manualMainGuest.phoneNumber, selectedCountry) ?
                      (selectedCountry.id === 'DO' ?
                        'Please enter a complete phone number (10 digits)' :
                        'Please enter a complete phone number'
                      ) : false
                    }
                  />
                </div>
              </div>
            </div>
          ) : (
            /* Companion Form */
            companions[currentCompanionIndex] && (
              <div className="mb-6 p-6" style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-8)' }}>
                <div className="grid gap-4" style={{ gridTemplateColumns: '1fr 1fr' }}>
                  <div className="fc">
                    <label>First Name</label>
                    <input
                      value={manualCompanions[currentCompanionIndex]?.firstName || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        updateManualCompanion(currentCompanionIndex, { firstName: e.target.value })
                      }
                      placeholder="Enter first name"
                    />
                  </div>
                  <div className="fc">
                    <label>Last Name</label>
                    <input
                      value={manualCompanions[currentCompanionIndex]?.lastName || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        updateManualCompanion(currentCompanionIndex, { lastName: e.target.value })
                      }
                      placeholder="Enter last name"
                    />
                  </div>
                </div>
              </div>
            )
          )}

          {scanAllowed && !sections.showScan && (
            <button
              type="button"
              className="text-xs underline font-medium mb-6"
              style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', color: 'var(--ink-700)' }}
              onClick={() => handleUseScan(activeGuestId)}
            >
              Scan an ID instead
            </button>
          )}
          </>
          )}

          </>
          )}

        {createError && <Notice variant="error" className="mt-4">{createError}</Notice>}
        <div className="flex gap-4 mt-6">
          <Button
              variant="primary"
              className="w-full"
              onClick={advance}
              disabled={
                currentCompanionIndex === -1 && canUseExistingClient && clientMode === 'existing'
                  ? !existingClient
                  :
                // Check if using scan or manual
                canProceedCurrentGuest()
                  ? false // Scan mode - enabled when current guest's document is ready
                  : ( // Manual mode - check manual input fields
                    currentCompanionIndex === -1
                      ? (!manualMainGuest.firstName || !manualMainGuest.lastName || !isValidPhoneNumber(manualMainGuest.phoneNumber, selectedCountry))
                      : (!manualCompanions[currentCompanionIndex]?.firstName || !manualCompanions[currentCompanionIndex]?.lastName)
                  )
              }
          >
            {Object.values(isAnalyzing).some(analyzing => analyzing) ? 'Analyzing documents...' : 'Next'}
          </Button>
        </div>
      </div>
    </div>

      {isCreatingInvitation && ReactDOM.createPortal(
          <div
            className="fixed inset-0 flex flex-col items-center justify-center"
            style={{ background: 'rgba(255, 255, 255, 0.8)', zIndex: 10002 }}
          >
            <Spinner />
            <div className="mt-4 text-base font-medium" style={{ color: 'var(--ink-900)' }}>Creating invitation...</div>
        </div>,
        document.body
      )}
    </>
  );
};
